import { useState } from "react";
import useDataset from "datasets/hooks/useDataset";
import Dataset from "datasets/types/interfaces/dataset";
import { resolveDatasetAccentColor } from "datasets/utils/color";
import { CardIllustration } from "~/assets/icons";
import useToast from "~/components/ui/toast";
import { ACCENT_PALETTE, resolveAccentPaletteColor } from "~/lib/accentPalette";
import { ColorSwatchGrid } from "~/pages/manifold-nextgen/components/ColorSwatchGrid";
import { FormDialog } from "~/pages/manifold-nextgen/components/FormDialog";
import { mutateDatasetList } from "~/services/catalog";
import { apiMutate } from "~/utils/apiClient";

const { VITE_CATALOG_API } = import.meta.env;

interface DatasetAppearanceDialogProps {
  dataset: Dataset;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const DatasetAppearanceDialog = ({
  dataset,
  open,
  onOpenChange,
}: DatasetAppearanceDialogProps) => {
  const { mutate } = useDataset();
  const { showSuccess, showError } = useToast();
  const [isSaving, setIsSaving] = useState(false);

  // Captured once per mount (the dialog remounts each time it opens), so it stays
  // stable as the Reset-equivalent baseline for the isDirty check below.
  const [committedColor] = useState(() => resolveDatasetAccentColor(dataset.accentColor).accent);
  const [color, setColor] = useState(committedColor);

  const isDirty = color !== committedColor;
  const previewColor = resolveAccentPaletteColor(color);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await apiMutate(`${VITE_CATALOG_API}/datasets/${dataset.id}`, "PATCH", {
        accentColor: color,
      });
      // `mutate()` only refreshes this single-dataset cache entry — list/card views
      // read a separate cached key and need their own revalidation to pick this up.
      await Promise.all([mutate(), mutateDatasetList()]);
      showSuccess("Appearance saved");
      onOpenChange(false);
    } catch {
      showError("Failed to save appearance");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Edit appearance"
      contentClassName="sm:max-w-2xl"
      onPrimary={handleSave}
      primaryLabel="Save"
      primaryPendingLabel="Saving…"
      primaryDisabled={!isDirty}
      isPending={isSaving}
    >
      <div className="grid grid-cols-[280px_1fr] gap-8 py-4">
        <div className="flex flex-col items-center gap-3">
          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Preview
          </span>
          <article className="relative flex h-[150px] w-[200px] flex-col rounded-lg border border-border bg-card shadow-[0_1px_2px_rgba(15,23,42,0.03)]">
            <div
              className="absolute inset-x-0 -top-px h-12 rounded-t-lg"
              style={{ backgroundColor: previewColor.bg }}
              aria-hidden="true"
            />
            <div className="relative mt-[34px] flex flex-1 flex-col gap-1.5 rounded-lg rounded-tl-none bg-card px-4 pb-3 pt-4">
              <CardIllustration
                aria-hidden="true"
                className="pointer-events-none absolute -top-6 left-0 z-10 h-[29px] w-[95px] text-card"
              />
              <p className="truncate text-sm font-semibold text-foreground">{dataset.title}</p>
            </div>
          </article>
        </div>

        <div className="flex flex-col gap-5">
          <div>
            <p className="mb-2 text-sm font-medium text-foreground">Color</p>
            <ColorSwatchGrid colors={ACCENT_PALETTE} color={color} onChange={setColor} />
          </div>
        </div>
      </div>
    </FormDialog>
  );
};
